/**
 * JSON-LD Schema Builders for Credit Cards
 * Multi-language support, follows schema.org/CreditCard spec
 * Enhanced with complete German templates
 */

import { CardCoreVM, keyLine, shortPitch } from "./cardCoreNormalizer";
import { generateBreadcrumbSchema } from "./schemaGenerator";

interface CardPageJsonLdOpts {
  core: CardCoreVM;
  url: string;
  imageUrl?: string;
  breadcrumb: Array<{ name: string; url: string }>;
  currency?: string;
}

interface SearchResultsJsonLdOpts {
  items: Array<{ core: CardCoreVM; url: string; image?: string }>;
  pageUrl: string;
  breadcrumb: Array<{ name: string; url: string }>;
  siteSearch?: { siteUrl: string; queryParam: string };
}

/**
 * Parse numeric value from formatted string
 * "21,99 %" → 21.99
 * "300 kr" → 300
 */
function parseNumber(str: string | undefined): number | undefined {
  if (!str) return undefined;
  
  // Explicit check for "Gratis"
  if (str.toLowerCase().includes('gratis')) return 0;
  
  // Extract numeric pattern: "21,99 %" → "21,99"
  const match = str.match(/[\d\s,]+/);
  if (!match) return undefined;
  
  // Remove spaces and convert comma to period
  const cleaned = match[0].replace(/\s/g, "").replace(",", ".");
  const parsed = parseFloat(cleaned);
  
  // Validate it's a real number
  return isNaN(parsed) ? undefined : parsed;
}

/**
 * Parse credit limit range
 * "5 000–150 000 kr" → { minValue: 5000, maxValue: 150000 }
 */
function parseCreditLimit(str: string | undefined): { minValue?: number; maxValue?: number } | undefined {
  if (!str) return undefined;
  
  const rangeMatch = str.match(/([\d\s]+)–([\d\s]+)/);
  if (rangeMatch) {
    return {
      minValue: parseInt(rangeMatch[1].replace(/\s/g, "")),
      maxValue: parseInt(rangeMatch[2].replace(/\s/g, ""))
    };
  }
  
  const fromMatch = str.match(/(?:Fra|ab)\s+([\d\s]+)/i);
  if (fromMatch) {
    return { minValue: parseInt(fromMatch[1].replace(/\s/g, "")) };
  }
  
  const maxMatch = str.match(/(?:Maks|bis)\s+([\d\s]+)/i);
  if (maxMatch) {
    return { maxValue: parseInt(maxMatch[1].replace(/\s/g, "")) };
  }
  
  return undefined;
}

/**
 * Generates JSON-LD for card detail page
 * Returns: [CreditCard, BreadcrumbList]
 * Enhanced with full Product and FinancialProduct types
 */
export function cardPageJsonLd(opts: CardPageJsonLdOpts): Array<Record<string, any>> {
  const { core, url, imageUrl, breadcrumb, currency = 'EUR' } = opts;
  
  // Parse numeric values
  const annualPercentageRate = parseNumber(core.effectiveRate);
  const annualFeeValue = core.annualFee === "Gratis" ? 0 : parseNumber(core.annualFee);
  const creditLimitRange = parseCreditLimit(core.creditLimit);
  const interestFreeDays = core.interestFree ? parseInt(core.interestFree.replace(/\D/g, "")) : undefined;
  
  // Build enhanced fees description (German only)
  const feesParts: string[] = [];
  
  if (core.annualFee === "Gratis" || annualFeeValue === 0) {
    feesParts.push("Keine Jahresgebühr");
  } else if (core.annualFee) {
    feesParts.push(`Jahresgebühr ${core.annualFee}`);
  }
  
  if (interestFreeDays) {
    feesParts.push(`bis zu ${interestFreeDays} zinsfreie Tage`);
  }
  
  const feesDescription = feesParts.join(", ");
  
  // Build comprehensive additional properties
  const additionalProperty: any[] = [];
  
  // Interest-free days
  if (interestFreeDays) {
    additionalProperty.push({
      "@type": "PropertyValue",
      "name": "Zinsfreie Tage",
      "value": `${interestFreeDays}`
    });
  }
  
  // Credit limit
  if (creditLimitRange) {
    const limitText = creditLimitRange.minValue && creditLimitRange.maxValue 
      ? `${creditLimitRange.minValue.toLocaleString('de-DE')}–${creditLimitRange.maxValue.toLocaleString('de-DE')} €`
      : creditLimitRange.minValue 
        ? `ab ${creditLimitRange.minValue.toLocaleString('de-DE')} €`
        : creditLimitRange.maxValue
          ? `bis ${creditLimitRange.maxValue.toLocaleString('de-DE')} €`
          : undefined;
    
    if (limitText) {
      additionalProperty.push({
        "@type": "PropertyValue",
        "name": "Kreditrahmen",
        "value": limitText
      });
    }
  }
  
  // Travel insurance
  if (core.travelInsurance) {
    additionalProperty.push({
      "@type": "PropertyValue",
      "name": "Reiseversicherung",
      "value": "Ja"
    });
  }
  
  // Enhanced CreditCard schema with full @type array
  const creditCardSchema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": ["CreditCard", "FinancialProduct", "Product"],
    "@id": `${url}#creditcard`,
    "name": core.name,
    "description": shortPitch(core) || keyLine(core) || `${core.name} – Kreditkarte von ${core.issuer}`,
    "category": "Kreditkarte",
    "brand": {
      "@type": "Brand",
      "name": "Visa/Mastercard" // Generic - network info not in CardCoreVM
    },
    "manufacturer": {
      "@type": "Organization",
      "name": core.issuer
    },
    "issuer": {
      "@type": "Organization",
      "name": core.issuer,
      // Construct bank URL (fallback - can be enhanced with real data)
      "url": `https://www.${core.issuer.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '')}.de`
    },
    "url": url,
    "feesAndCommissionsSpecification": feesDescription
  };
  
  // Add image
  if (imageUrl) {
    creditCardSchema.image = imageUrl;
  }
  
  // Add interest rate as QuantitativeValue (per your template)
  if (annualPercentageRate != null) {
    creditCardSchema.interestRate = {
      "@type": "QuantitativeValue",
      "name": "Effektiver Jahreszins",
      "value": annualPercentageRate,
      "unitText": "%"
    };
    
    // Also keep backwards compatibility
    creditCardSchema.annualPercentageRate = annualPercentageRate;
  }
  
  // Add additional properties array
  if (additionalProperty.length > 0) {
    creditCardSchema.additionalProperty = additionalProperty;
  }
  
  // Offers (price specification) - Enhanced
  if (typeof annualFeeValue === 'number' && !isNaN(annualFeeValue)) {
    creditCardSchema.offers = {
      "@type": "Offer",
      "price": annualFeeValue,
      "priceCurrency": currency,
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "name": "Jahresgebühr",
        "price": annualFeeValue,
        "priceCurrency": currency
      },
      "availability": "https://schema.org/InStock",
      "url": url
    };
    
    if (annualFeeValue === 0) {
      creditCardSchema.offers.isAccessibleForFree = true;
    }
  }
  
  // Credit limit as MonetaryAmount
  if (creditLimitRange) {
    creditCardSchema.creditLimit = {
      "@type": "MonetaryAmount",
      "currency": currency,
      ...creditLimitRange
    };
  }
  
  // Placeholder for aggregateRating (can be populated from database)
  // Uncomment and populate when you have rating data:
  // creditCardSchema.aggregateRating = {
  //   "@type": "AggregateRating",
  //   "ratingValue": "4.5",
  //   "reviewCount": "127"
  // };
  
  // Breadcrumb
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumb);
  
  return [creditCardSchema, breadcrumbSchema];
}

/**
 * Generates JSON-LD for search results / card listing page
 * Returns: [ItemList, BreadcrumbList, WebSite (optional)]
 */
export function searchResultsJsonLd(opts: SearchResultsJsonLdOpts & { listName?: string; listDescription?: string }): Array<Record<string, any>> {
  const { items, pageUrl, breadcrumb, siteSearch, listName, listDescription } = opts;
  
  const schemas: Array<Record<string, any>> = [];
  
  // ItemList for cards
  const itemList: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": listName || "Deutsche Kreditkarten",
    "description": listDescription || `Vergleichen Sie ${items.length}+ deutsche Kreditkarten`,
    "numberOfItems": items.length,
    "itemListElement": items.map((item, idx) => {
      const pitch = shortPitch(item.core);
      const key = keyLine(item.core);
      const description = pitch || key || undefined;
      
      return {
        "@type": "ListItem",
        "position": idx + 1,
        "url": item.url,
        "name": item.core.name,
        ...(item.image && { image: item.image }),
        ...(description && { description })
      };
    })
  };
  
  schemas.push(itemList);
  
  // Breadcrumb
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumb);
  schemas.push(breadcrumbSchema);
  
  // WebSite with SearchAction
  if (siteSearch) {
    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "url": siteSearch.siteUrl,
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${siteSearch.siteUrl}?${siteSearch.queryParam}={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      }
    };
    schemas.push(websiteSchema);
  }
  
  return schemas;
}
